// Single source of truth for the API base URL.
//
// The backend serves under a global `/api` prefix, but `VITE_REACT_APP_API_URL`
// is set inconsistently across previews/worktrees/envs — some include `/api`,
// some don't. A missing `/api` silently 404s every API call (auth "Failed to
// send verification code", etc.); a doubled `/api` is just as broken.
//
// Normalize to exactly one trailing `/api` so the env value can be host-only OR
// host+`/api` and API calls resolve either way. All API-request consumers
// (axios instance, auth service, dev-session) read this.
const RAW = (import.meta.env.VITE_REACT_APP_API_URL || 'http://localhost:3040')
  .replace(/\/+$/, '')

export const API_BASE_URL = /\/api$/.test(RAW) ? RAW : `${RAW}/api`

/**
 * The API base for requests that SET or CARRY the HttpOnly platform-session cookie: the
 * sign-in XHRs (magic-auth verify, password login, 2FA challenge verify, the OAuth `code`
 * exchange) and the renew / sign-out endpoints. Everything else keeps `API_BASE_URL`.
 *
 * WHY A SECOND BASE (2026-10-11, "asked for the authenticator every time"): production and
 * dev build the platform with `VITE_REACT_APP_API_URL` = the API's own Cloud Run URL
 * (`https://vera-prod-api-….a.run.app`). `run.app` is on the Public Suffix List, so that host
 * is CROSS-SITE to `platform.vera.inc`. The session cookie is `SameSite=Strict`, and a
 * browser neither stores a SameSite cookie from a cross-site XHR response nor sends one on a
 * cross-site XHR. So the cookie set by `/auth/2fa/challenge/verify` never existed, every
 * `/auth/session/refresh` answered 401 "No session", and every visit after the 15-minute
 * access token lapsed was a full sign-in with the second factor.
 *
 * `api.<env>.vera.inc` is a Cloud Run domain mapping of the SAME service (the WorkOS
 * callback already lands there), and its CORS answers the platform origin with
 * `Access-Control-Allow-Credentials: true`. Sending the cookie-bearing calls there makes them
 * same-site, which is the premise `platform-session-cookie.ts` was written on.
 *
 * Narrow on purpose: only a `*.run.app` API seen from a `platform.<x>.vera.inc` page is
 * remapped (to `api.<x>.vera.inc`). A same-site API URL, localhost, and any other host are
 * returned unchanged — and if the build is later pointed at `api.vera.inc` this is a no-op.
 */
export const resolveSessionApiBaseUrl = (
  apiBaseUrl: string,
  pageHostname: string | undefined,
): string => {
  if (!pageHostname) return apiBaseUrl
  let apiHost: string
  try {
    apiHost = new URL(apiBaseUrl).hostname
  } catch {
    return apiBaseUrl
  }
  if (!apiHost.endsWith('.run.app')) return apiBaseUrl
  const page = pageHostname.toLowerCase()
  const match = /^platform\.((?:[a-z0-9-]+\.)*vera\.inc)$/.exec(page)
  if (!match) return apiBaseUrl
  return `https://api.${match[1]}/api`
}

export const SESSION_API_BASE_URL = resolveSessionApiBaseUrl(
  API_BASE_URL,
  typeof window !== 'undefined' ? window.location?.hostname : undefined,
)

/**
 * Is this API URL a LOCAL development target?
 *
 * Gates the dev-only auth bypasses (VITE_DEV_SESSION / VITE_SKIP_AUTH), whose real
 * requirement is "never point a bypass at a deployed environment" — not "the string
 * must contain localhost".
 *
 * A loopback-only check made physical-device testing impossible: a phone cannot reach
 * the Mac's `localhost`, so driving the scan-session flow from a real handset requires
 * the machine's LAN address, and the old check disabled the bypass exactly when it was
 * needed. Worse, the guard `throw`s at module load, so the app rendered nothing at all
 * rather than reporting a login problem.
 *
 * Private ranges are enumerated rather than pattern-guessed — note 172.16–172.31 only,
 * since 172.32+ is public address space.
 */
export const isLocalApiUrl = (url: string | undefined): boolean => {
  if (!url) return false
  let host: string
  try {
    host = new URL(/^https?:\/\//.test(url) ? url : `http://${url}`).hostname
  } catch {
    return false
  }
  if (host === 'localhost' || host.endsWith('.localhost')) return true
  if (host === '127.0.0.1' || host === '::1' || host === '[::1]') return true
  if (/^10\./.test(host)) return true
  if (/^192\.168\./.test(host)) return true
  if (/^172\.(1[6-9]|2\d|3[01])\./.test(host)) return true
  return false
}
